import React, { useState, useEffect } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  Tooltip,
} from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import AddIcon from '@material-ui/icons/Add';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckCircleOutline from '@material-ui/icons/CheckCircleOutline';
import { useHistory, useLocation } from 'react-router-dom';
import { css } from '@emotion/core';

import Spinner from '../../util/Spinner';
import { useRootStore } from '../../../stores/BridgeProvider';
import storageService from '../../../services/storage.service';
import { FlexDiv, WideBox } from '../../styles/styles';
import DMLogo from '../../../assets/DEAL MAP-LOGO.png';
import { COLORS } from '../../util/Colors';
import XeroLogo from '../../../assets/xero/XeroLogo';

const Link = css`
  color: ${COLORS.blue};
  :hover {
    cursor: pointer;
  }
`;

const SelectOrg = () => {
  const history = useHistory();
  const location = useLocation();

  const { state } = location;
  const { authStore, orgStore, dataStore, dealMapperStore } = useRootStore();
  const [isLoading, setLoading] = useState(true);
  const [orgList, setOrgList] = useState([]);
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState('org-select');

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const fetchOrgs = async () => {
    const { data } = await authStore.request.get(
      `${process.env.REACT_APP_API}/api/organizations`,
    );
    setOrgList(data);
  };
  const fetchInvites = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/invitations`,
      );
      setInvites(data);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    const initialize = async () => {
      await Promise.all([fetchOrgs(), fetchInvites()]);
      if (state !== undefined) {
        setSelectedTab(state.currentTab);
      }
      setLoading(false);
    };
    initialize();
  }, []);
  const setActiveOrg = async orgId => {
    try {
      setLoading(true);
      await authStore.request.post(
        `${process.env.REACT_APP_API}/api/active-organization`,
        {
          orgId,
        },
      );
      storageService.setItem('activeOrg', orgId);
      orgStore.reset();
      dealMapperStore.reset();
      dataStore.reset();
      // FIXME: confirm get new config
      orgStore.retrieveConfig();
      authStore.setNotifications([]);
      history.push('/dashboard');
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };
  const acceptInvite = async id => {
    try {
      await authStore.request.post(
        `${process.env.REACT_APP_API}/api/organization/accept-invitation`,
        { invitationId: id },
      );
      fetchInvites();
      fetchOrgs();
    } catch (e) {
      console.error(e);
    }
  };
  const rejectInvite = async id => {
    try {
      await authStore.request.delete(
        `${process.env.REACT_APP_API}/api/invitation`,
        {
          data: { invitationId: id },
        },
      );
      fetchInvites();
    } catch (e) {
      console.error(e);
    }
  };
  const getXeroUrl = async () => {
    try {
      setLoading(true);
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/xero-url`,
      );
      window.location = data;
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };
  if (isLoading) return <Spinner />;

  return (
    <FlexDiv>
      <WideBox>
        <Typography variant="h3" gutterBottom>
          Select an Organization
        </Typography>
        <Divider />
        <div style={{ display: 'flex', minHeight: '400px' }}>
          <List
            style={{
              width: '350px',
              borderRight: 'solid 0.5px rgb(224, 224, 224)',
              marginTop: '10px',
            }}
          >
            <ListItem
              button
              selected={selectedTab === 'org-select'}
              onClick={() => setSelectedTab('org-select')}
            >
              <ListItemText
                primary="My Organizations"
                secondary="Select from a list of existing organizations"
              />
            </ListItem>
            <ListItem
              button
              selected={selectedTab === 'org-create'}
              onClick={() => setSelectedTab('org-create')}
            >
              <ListItemText
                primary="Create New Organization"
                secondary="Create an organization manually or by integrating 
                with a third party accounting system"
              />
            </ListItem>
            <ListItem
              button
              selected={selectedTab === 'org-invitations'}
              onClick={() => setSelectedTab('org-invitations')}
            >
              <ListItemText
                primary="Invitations to Organizations"
                secondary="View and manage invitations to other organizations"
              />
            </ListItem>
          </List>
          {selectedTab === 'org-select' && (
            <div style={{ width: '100%' }}>
              {orgList.length === 0 ? (
                <div style={{ margin: '60px' }}>
                  <div>
                    <span>
                      {' '}
                      <img src={DMLogo} alt="dealmap logo" width="70px" />{' '}
                      <h4>Welcome to Deal Map!</h4>{' '}
                    </span>
                  </div>
                  <p>
                    This page is the central hub to your organizations, here you
                    may select between existing organizations, create a new
                    organization by integrating with an accounting system, and
                    view and respond to invitations to other organizations.
                  </p>
                  You are not a part of an organization,{' '}
                  <span
                    onClick={() => setSelectedTab('org-invitations')}
                    css={Link}
                  >
                    check invites
                  </span>{' '}
                  or{' '}
                  <span onClick={() => setSelectedTab('org-create')} css={Link}>
                    create a new organization
                  </span>
                </div>
              ) : (
                <List style={{ width: '400px', margin: 'auto' }}>
                  <h3>My Organizations</h3>
                  {orgList.map(org => (
                    <ListItem
                      key={org.OrgId}
                      button
                      onClick={() => setActiveOrg(org.OrgId)}
                    >
                      <ListItemText
                        primary={org.Name}
                        primaryTypographyProps={{ variant: 'h5' }}
                      />
                      <ListItemIcon>
                        <LaunchIcon />
                      </ListItemIcon>
                    </ListItem>
                  ))}
                </List>
              )}
            </div>
          )}
          {selectedTab === 'org-create' && (
            <div style={{ width: '100%' }}>
              <h3>Create New Organization</h3>
              <List style={{ width: '400px', margin: 'auto' }}>
                <ListItem>
                  <ListItemText
                    primary={
                      <div>
                        Link to an Accounting System{' '}
                        <Tooltip
                          title="Grant permission to Deal Map to connect with your current 
                          accounting system to import the information needed to setup a 
                          new organization in Deal Map that will work 
                          seamlessly with your accounting system"
                          placement="top-start"
                        >
                          <HelpOutlineIcon
                            style={{ fontSize: '14px', color: COLORS.blue }}
                          />
                        </Tooltip>
                      </div>
                    }
                    primaryTypographyProps={{ variant: 'h5' }}
                    secondary="Create an organization by pulling from an existing accounting 
                    system ***Recommended"
                  />
                </ListItem>

                <ListItem
                  button
                  onClick={getXeroUrl}
                  style={{ justifyContent: 'center', padding: '0' }}
                >
                  <XeroLogo />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Create Organization Manually"
                    primaryTypographyProps={{ variant: 'h5' }}
                    secondary="Create an organization without importing existing accounts. 
                    You may still integrate with an accounting system if you so choose."
                  />
                </ListItem>
                <ListItem
                  button
                  onClick={() => history.push('/create-organization')}
                >
                  <ListItemText
                    primary="Create Organization"
                    primaryTypographyProps={{ variant: 'h6' }}
                  />
                  <ListItemIcon>
                    <AddIcon />
                  </ListItemIcon>
                </ListItem>
              </List>
            </div>
          )}
          {selectedTab === 'org-invitations' && (
            <div style={{ width: '100%' }}>
              <h3>Organization Invitations</h3>
              {invites.length === 0 ? (
                <div style={{ margin: '60px' }}>
                  You have no pending invitations at the moment
                </div>
              ) : (
                <List style={{ width: '400px', margin: 'auto' }}>
                  {invites.map(({ Name, InvitationId }) => (
                    <ListItem key={InvitationId}>
                      <ListItemText primary={Name} />
                      <ListItemIcon>
                        <IconButton
                          aria-label="Delete"
                          onClick={handleClickOpen}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemIcon>
                      <ListItemIcon>
                        <IconButton
                          aria-label="Accept"
                          onClick={() => {
                            acceptInvite(InvitationId);
                            setSelectedTab('org-select');
                          }}
                        >
                          <CheckCircleOutline />
                        </IconButton>
                      </ListItemIcon>
                      <Dialog
                        open={open}
                        onClose={handleClose}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                      >
                        <DialogTitle id="alert-dialog-title">
                          {`Do you really want to delete?`}
                        </DialogTitle>
                        <DialogActions>
                          <Button onClick={handleClose} color="primary">
                            Cancel
                          </Button>
                          <Button
                            onClick={() => rejectInvite(InvitationId)}
                            color="primary"
                            autoFocus
                          >
                            Delete
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </ListItem>
                  ))}
                </List>
              )}
            </div>
          )}
        </div>
      </WideBox>
    </FlexDiv>
  );
};

export default SelectOrg;
